import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { TrendingUp, TrendingDown, Wallet, Percent, ShieldCheck, AlertTriangle } from "lucide-react";
import { api, money } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { FilterBar } from "@/components/FilterBar";

const Kpi = ({ label, value, sub, tone = "neutral", icon: Icon, testid }) => (
  <div className="border border-slate-200 bg-white p-5" data-testid={testid}>
    <div className="flex items-start justify-between">
      <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</span>
      {Icon && <Icon className="h-4 w-4 text-slate-400" />}
    </div>
    <div
      className={`mt-3 font-display text-3xl font-black tracking-tight ${
        tone === "profit" ? "text-emerald-600" : tone === "loss" ? "text-red-600" : "text-slate-900"
      }`}
    >
      {value}
    </div>
    {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
  </div>
);

const Panel = ({ title, action, children, testid }) => (
  <div className="border border-slate-200 bg-white" data-testid={testid}>
    <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
      <h3 className="font-display text-base font-bold tracking-tight">{title}</h3>
      {action}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const Leaderboard = ({ rows, currency, testid }) => (
  <div className="divide-y divide-slate-100" data-testid={testid}>
    {rows.length === 0 && <div className="py-6 text-sm text-slate-500">No data for this range.</div>}
    {rows.map((r, i) => (
      <div key={r.id || i} className="flex items-center justify-between py-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="w-6 text-xs font-bold text-slate-400">{String(i + 1).padStart(2, "0")}</span>
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold">{r.name}</div>
            <div className="text-xs text-slate-500">
              Spend {money(r.spend, currency)} · ROI {r.roi}%
            </div>
          </div>
        </div>
        <div
          className={`text-sm font-bold ${r.profit >= 0 ? "text-emerald-600" : "text-red-600"}`}
        >
          {money(r.profit, currency)}
        </div>
      </div>
    ))}
  </div>
);

export default function Dashboard() {
  const { filters } = useData();
  const [data, setData] = useState(null);
  const [due, setDue] = useState(null);

  useEffect(() => {
    const params = { ...filters };
    api
      .get("/analytics/summary", { params })
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [filters]);

  useEffect(() => {
    api.get("/reminders").then((r) => setDue(r.data.counts)).catch(() => {});
  }, []);

  const k = data?.kpis;
  const cur = filters.currency;

  return (
    <div data-testid="dashboard-page">
      <div className="mb-8">
        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
          Performance Overview
        </div>
        <h1 className="mt-2 font-display text-4xl font-black tracking-tight sm:text-5xl">
          Profit &amp; Loss Desk
        </h1>
      </div>

      <FilterBar />

      {due?.total > 0 && (
        <div
          className="mb-8 flex flex-wrap items-center justify-between gap-4 border-l-4 border-red-600 border-y border-r border-slate-200 bg-red-50 px-5 py-4"
          data-testid="reminder-banner"
        >
          <div className="text-sm">
            <span className="font-display text-base font-bold">
              {due.total} follow-up{due.total === 1 ? "" : "s"} waiting today
            </span>
            <span className="ml-2 text-slate-600">
              {due.validations} validation · {due.payments} campaign payment · {due.invoices} invoice
              past their deadline
            </span>
          </div>
          <Link
            to="/reminders"
            data-testid="reminder-banner-link"
            className="bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
          >
            Review reminders
          </Link>
        </div>
      )}

      {data?.kpis?.consolidated && (
        <p className="mb-6 text-xs text-slate-500" data-testid="consolidated-note">
          Showing INR and USD combined in {data.kpis.currency} at 1 USD = ₹{data.kpis.fx_rate_usd_inr}.
          Change the rate under Settings → Exchange Rate.
        </p>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4 lg:gap-6">
        <Kpi label="Total Spend" value={money(k?.spend, cur)} sub={`${k?.entries || 0} entries`} icon={Wallet} testid="kpi-spend" />
        <Kpi label="Total Revenue" value={money(k?.revenue, cur)} icon={TrendingUp} testid="kpi-revenue" />
        <Kpi
          label="Net Profit / Loss"
          value={money(k?.profit, cur)}
          tone={(k?.profit || 0) >= 0 ? "profit" : "loss"}
          icon={(k?.profit || 0) >= 0 ? TrendingUp : TrendingDown}
          testid="kpi-profit"
        />
        <Kpi label="ROI" value={`${k?.roi ?? 0}%`} tone={(k?.roi || 0) >= 0 ? "profit" : "loss"} icon={Percent} testid="kpi-roi" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Panel title={`Spend vs Revenue (${filters.granularity})`} testid="chart-trend">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={data?.series || []}>
                <CartesianGrid stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} stroke="#64748B" />
                <YAxis tick={{ fontSize: 11 }} stroke="#64748B" />
                <Tooltip contentStyle={{ background: "#0F172A", border: "none", color: "#fff", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="spend" stroke="#0F172A" fill="#CBD5E1" name="Spend" />
                <Area type="monotone" dataKey="revenue" stroke="#10B981" fill="#D1FAE5" name="Revenue" />
              </AreaChart>
            </ResponsiveContainer>
          </Panel>
        </div>
        <Panel title="Validation & Payments" testid="panel-validation">
          <div className="space-y-3 text-sm">
            {[
              ["Validated entries", data?.validation?.validated || 0, "text-emerald-600", ShieldCheck],
              ["Pending validation", data?.validation?.pending || 0, "text-amber-600", AlertTriangle],
              ["Disputed", data?.validation?.disputed || 0, "text-red-600", AlertTriangle],
            ].map(([label, value, tone, Icon]) => (
              <div key={label} className="flex items-center justify-between border-b border-slate-100 pb-3">
                <span className="flex items-center gap-2 text-slate-600">
                  <Icon className={`h-4 w-4 ${tone}`} /> {label}
                </span>
                <span className="font-bold">{value}</span>
              </div>
            ))}
            <div className="pt-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
              Invoices
            </div>
            {[
              ["Outstanding", money(data?.invoices?.outstanding, cur)],
              ["Collected", money(data?.invoices?.collected, cur)],
              ["Unpaid invoices", data?.invoices?.unpaid || 0],
              ["Partially paid", data?.invoices?.partial || 0],
              ["Fully paid", data?.invoices?.paid || 0],
            ].map(([label, value]) => (
              <div key={label} className="flex items-center justify-between border-b border-slate-100 pb-3">
                <span className="text-slate-600">{label}</span>
                <span className="font-bold">{value}</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Panel title="Top Campaigns" testid="panel-top-campaigns">
          <Leaderboard rows={data?.top_campaigns || []} currency={cur} testid="top-campaigns-list" />
        </Panel>
        <Panel title="Top Clients" testid="panel-top-clients">
          <Leaderboard rows={data?.top_clients || []} currency={cur} testid="top-clients-list" />
        </Panel>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Panel title="Profit by Manager" testid="panel-managers">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data?.managers || []}>
              <CartesianGrid stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#64748B" />
              <YAxis tick={{ fontSize: 11 }} stroke="#64748B" />
              <Tooltip contentStyle={{ background: "#0F172A", border: "none", color: "#fff", fontSize: 12 }} />
              <Bar dataKey="profit" fill="#0F172A" name="Profit" />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Profit by Via" testid="panel-vias">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data?.vias || []}>
              <CartesianGrid stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#64748B" />
              <YAxis tick={{ fontSize: 11 }} stroke="#64748B" />
              <Tooltip contentStyle={{ background: "#0F172A", border: "none", color: "#fff", fontSize: 12 }} />
              <Line type="monotone" dataKey="profit" stroke="#10B981" strokeWidth={2} name="Profit" />
              <Line type="monotone" dataKey="spend" stroke="#EF4444" strokeWidth={2} name="Spend" />
            </LineChart>
          </ResponsiveContainer>
        </Panel>
      </div>
    </div>
  );
}
